import { useCallback, useEffect, useRef, useState } from 'react';

import {
  paintWaveform,
  positionFromX,
  type WaveformParams,
} from '../../audio/waveform-render';
import type { WaveformData } from '../../audio/waveform-data';
import { formatDuration } from '../format';

type WaveformProps = {
  data: WaveformData | null;
  params: WaveformParams;
  progress: number;
  duration: number;
  onSeek(fraction: number): void;
  label?: string;
};

/**
 * The waveform *is* the seek control, as on SoundCloud — there is no separate scrubber
 * in the track view. Click or drag anywhere to seek; hovering previews the time under
 * the cursor.
 */
export function Waveform({
  data,
  params,
  progress,
  duration,
  onSeek,
  label = 'Seek within track',
}: WaveformProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const draggingRef = useRef(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ width: Math.round(width), height: Math.round(height) });
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context || size.width === 0 || size.height === 0) return;

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(size.width * ratio);
    canvas.height = Math.round(size.height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);

    const styles = getComputedStyle(canvas);
    paintWaveform(context, {
      data,
      params,
      progress,
      hover,
      width: size.width,
      height: size.height,
      devicePixelRatio: ratio,
      placeholderColor: styles.getPropertyValue('--dimmer').trim() || '#4a4a4a',
      playheadColor: styles.getPropertyValue('--wave-playhead').trim() || '#f2f0eb',
    });
  }, [data, hover, params, progress, size.height, size.width]);

  const fractionFromEvent = useCallback((clientX: number): number => {
    const host = hostRef.current;
    if (!host) return 0;
    const rect = host.getBoundingClientRect();
    return positionFromX(clientX - rect.left, rect.width);
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      draggingRef.current = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      onSeek(fractionFromEvent(event.clientX));
    },
    [fractionFromEvent, onSeek],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const fraction = fractionFromEvent(event.clientX);
      setHover(fraction);
      if (draggingRef.current) onSeek(fraction);
    },
    [fractionFromEvent, onSeek],
  );

  const endDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (duration <= 0) return;
      const step = event.shiftKey ? 60 : 5;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onSeek(Math.max(0, (progress * duration - step) / duration));
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        onSeek(Math.min(1, (progress * duration + step) / duration));
      } else if (event.key === 'Home') {
        event.preventDefault();
        onSeek(0);
      }
    },
    [duration, onSeek, progress],
  );

  return (
    <div
      ref={hostRef}
      className="waveform"
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(progress * duration)}
      aria-valuetext={formatDuration(progress * duration)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={() => setHover(null)}
      onKeyDown={onKeyDown}
    >
      <canvas ref={canvasRef} aria-hidden="true" />
      {hover !== null && duration > 0 && (
        <span className="waveform-tooltip numeric" style={{ left: `${hover * 100}%` }}>
          {formatDuration(hover * duration)}
        </span>
      )}
    </div>
  );
}
